import { BagContent, OrderApiException, OrderErrorCode, Promotion, Reward } from '@/models';
import Vue from 'vue';
import { Component, Watch} from 'vue-property-decorator';
import { AccountManagementEvent } from '@/services/analytics/events';
import { InteractionTrackingType } from '@/services/analytics/platforms/adobe-analytics/models';
import { formatOrderDate, getCountryCodeByTopLevelDomain } from '@/helpers';
import { AnalyticsEventAction, AnalyticsEventCategory, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { ButtonType } from '../CmgButton/cmg-button';
import moment from 'moment';

@Component({
    name: 'CmgRewardsList',
})
export default class CmgRewardsListModal extends Vue {    
  bagContent: BagContent | null = null;
  selectedReward: any;
  position: number | undefined;
  rewardButtonText = 'USE REWARD';
  displayError = false;
  combinedRewards: any[] = [];
  hasTrackedModalRewardCtaPresentation = false;

  get isUpdatedContextualRewardsBagUpdatesEnabled(): boolean {
    return this.$appStore.state.featureFlags.featureFlags.isUpdatedContextualRewardsBagUpdatesEnabled;
  }

  get rewards() {
    return (this.$appStore.state.ui.modals.rewardsListModal.args?.rewards || this.$appStore.state.customer.wallet.promotions);
  }

  get visible(): boolean {
    return !!this.$appStore.state.ui.modals.rewardsListModal.visible;
  }

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }

  get modalWidth(): string {
    return this.bagRewards.length > 1 ? '52em' : '30em';
  }

  get bagRewards(): Reward[] {
    return this.$appStore.state.reward.bagRewards;
  }
  
  get rewardToastVisible() {
    return this.$appStore.state.ui.bag.bagToastVisibility;
  }

  get pointsBalance(): number {
    return this.$services.reward.getPointsBalance();
  }

  get headerText(): string {
    if(!this.isUpdatedContextualRewardsBagUpdatesEnabled){
      return this.bagContent?.viewPromoModal ? `${this.rewards.length} ${this.bagContent?.viewPromoModal}` : `${this.rewards.length} REWARDS`;
    }
    return 'CHOOSE A REWARD'
  }

  get subText(): string {
    return this.bagContent?.viewPromoModalDisclaimer || 'Limit one reward per order.';
  }

  get isCombinedWalletAndPointsRewards(): boolean {
    return this.bagRewards.length > 0 && this.rewards.length > 0;
  }

  get isContextualRewardsOnly(): boolean {
    return this.bagRewards.length > 0 && this.rewards.length <= 0;
  }

  get checkedCircleUrl(): string {
    return this.$appStore.state.cms.images.icons.checkedCircle.default;
  }

  CombinedWalletAndPointsRewards() {
    let bagRewardsArr = this.bagRewards.map(reward => ({
      ...reward,
      campaign: '',
      expirationDate: '',
      expiringSoon: '',
      mobileImage: reward.mediaUri,
      mobileThumb: reward.mediaUri,
      promotionActivity: null,
      promotionCampaignTags: [],
      promotionCode: '',
      promotionDescription: reward.description,
      promotionName: reward.title,
      promotionId: parseInt(reward.id),
      termsAndConditions: reward.terms,
      webImage: reward.mediaUri,
      webThumb: reward.mediaUri,
      isSelected: false,
    })).sort((a, b) => b.points - a.points);
    
    let rewardsArr: any[] = this.rewards;
    this.combinedRewards = rewardsArr.concat(bagRewardsArr);
  }

  useSelectedReward() {
    this.displayError = false;

    if(!this.selectedReward){
      this.displayError = true;
      return;
    }
    //wallet reward
    if(this.selectedReward.promotionCode.length > 0){
      this.onClickAddReward(this.selectedReward);
    }
    //point redemption
    if(this.selectedReward.offerType && this.selectedReward.offerType === "ContextualBag"){
      this.onClickRedeem(this.selectedReward);
    }
    this.clearSelectedRewards();
  }

  onClickRedeem(reward: Reward) {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardDetailModal,
      action: reward.title + AnalyticsEventAction.RewardRedeemClick,
      label: AnalyticsEventLabel.RewardDetailModalSpecific + reward.title,
    });
    this.$services.ui.setAppPepperVisibility(true);
    this.$services.reward
      .purchaseReward(reward.id)
      .then(async purchasedReward => ({
        purchasedReward,
        reward,
        promotion: await this.$services.promotions.getPromotionDetails(purchasedReward?.promotionCode || '')
      }))
      .then((r) => {
        if (r.purchasedReward && r.reward) {
          // if (this.args && this.args.onRedeem) {
          //   this.args?.onRedeem();
          // } else {
            this.$services.ui.setModalVisibility('rewardRedemptionSuccess', true, {
              purchasedReward: r.purchasedReward,
              reward,
            });
            this.$services.analytics.send(new AccountManagementEvent(
              'rewardsAddedToWallet',
              undefined,
              reward.title,
              'account',
              undefined,
              undefined,
              undefined,
              undefined,
              'account:rewards:home',
              'favorited-reward-layer',
            ));
          // }
        }
      })
      .catch(error => {
        if (this.$services.reward.isSparkflyError(error)) {
          this.$services.ui.setModalVisibility('rewardRedemptionSuccess', true, { reward, showErrorMessage: true });
          this.$services.reward.setShouldShowRefresh(true);
        } else {
          const onClose = () => {
            this.$services.ui.setModalVisibility('basicError', false);
            this.$services.ui.setModalVisibility('rewardDetail', true, { reward });
          };

          this.$services.googleAnalytics.sendCustomEvent({
            category: AnalyticsEventCategory.RewardDetailModal,
            action: AnalyticsEventAction.PurchaseError,
            label: AnalyticsEventLabel.PurchaseError,
          });

          this.$services.ui.setModalVisibility('basicError', true, {
            header: `OUR BAD`,
            content: 'There was a problem getting your reward. Give it a sec and try again.',
            background: 'white',
            options: [
              {
                text: 'Okay',
                type: ButtonType.Primary,
                onClick: onClose,
              },
            ],
            onClose,
          });
        }
      })
      .finally(() => {
        this.$services.loyalty.refreshPoints(true);
        this.$services.promotions.refreshCustomerPromotions();
        this.$services.ui.setAppPepperVisibility(false);
        this.onClose();
      });
  }

  async onClickAddReward(reward: Promotion) {
    this.$services.ui.setLastSelectedElement('bag');
    this.$services.ui.setAppPepperVisibility(true);

    if(this.isUpdatedContextualRewardsBagUpdatesEnabled){
      this.$services.analytics.send(new AccountManagementEvent(
        InteractionTrackingType.RewardsAddedToBag,
        undefined,
        reward.promotionName,
        'Rewards List',
        reward.promotionCode,
        'rewards-bag',
        reward.campaign,
        undefined,
        'order:cart',
      ));
    } else {
      this.$services.analytics.send(new AccountManagementEvent(InteractionTrackingType.RewardsAddedToBag, undefined, reward.promotionName, 'Rewards List'))
    }

    let order = this.$services.order.getCurrentOrder();
    const promotionCode = reward.promotionCode;

    if (!order) {
      if (this.$appStore.state.restaurant.selectedRestaurant) {
        order = await this.$services.order.createOrder(this.$appStore.state.restaurant.selectedRestaurant!.restaurantNumber);
      } else {

        this.$services.ui.setModalVisibility('findAChipotle', true, {
          onClose: async () => {
            if (!order || !this.$appStore.state.restaurant.selectedRestaurant) {
              return;
            }

            try {
              order = await this.$services.order.createOrder(this.$appStore.state.restaurant.selectedRestaurant.restaurantNumber,);

              if (order && promotionCode) {
                await this.$services.order.applyDiscountToOrder(order, promotionCode);

                this.$services.bag.setBagVisibility(true);
                if(!this.isUpdatedContextualRewardsBagUpdatesEnabled){
                  this.displayRewardToast(true);
                }
              }
            } catch (e: any) {
              this.handleAddToBagException(e);
            }
          },
        });

        this.$services.ui.setModalVisibility('toast', true, {
          body: 'Before you order, we gotta find your Chipotle first.'.toUpperCase(),
          bodyOnly: true,
        });
        Vue.nextTick(() => this.$services.ui.focusFirstDiv('.cmg-modal-container'));
        return;
      }
    }

    if (!order) {
      return;
    }
    try {
      this.$services.ui.setModalVisibility('rewardsListModal', false);
      if (!order.discounts || !order.discounts.find(d => d.promotionCode === promotionCode)) {

        await this.$services.order.applyDiscountToOrder(order, promotionCode);

        this.$nextTick(() => {
          Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
          this.$services.ui.setLastSelectedElement('bag');
        });
      } else {
        await this.$services.order.removeDiscountFromOrder(order, promotionCode);

        Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
      }

    } catch (e: any) {
      this.$services.ui.setModalVisibility('basicError', true, {
        header: 'one reward or promo per order',
        content: `You’ve already applied a promo to your order. To use a reward instead, remove the promo first.`,
        options: [
          {
            text: `Remove Promo`,
            type: ButtonType.Primary,
            onClick: () => {
              this.$services.ui.setModalVisibility('basicError', false);
              if(order && order.discounts?.length) {
                this.$services.order.removeDiscountFromOrder(order, order.discounts[0].promotionCode).then(() => {
                  this.onClickAddReward(this.selectedReward);
                }).catch((e) => {
                  console.log('error removing promo code and adding selected reward', e)
                  this.handleAddToBagException(e);
                });
              }
            },
          },
          {
            text: 'Cancel',
            type: ButtonType.Secondary,
            onClick: () => {
              this.$services.ui.setModalVisibility('basicError', false)
            },
          },
        ],
      });
      this.handleAddToBagException(e);
    } finally {
      this.$services.ui.setAppPepperVisibility(false);
      if(!this.isUpdatedContextualRewardsBagUpdatesEnabled){
        this.displayRewardToast(true);
      }
      this.$emit('rewardAdded');
    }
  }

  private clearSelectedRewards() {
    const clearItems = (items: any[] = []) => {
      items.forEach(item => {
        if (item && item.isSelected) {
          Vue.set(item, 'isSelected', false);
        }
      });
    };

    clearItems(this.rewards as any[]);
    clearItems(this.bagRewards as any[]);
    clearItems(this.combinedRewards);
  }

  onRewardSelectedClick(reward: any, position: any) {
    this.clearSelectedRewards();

    if (reward) {
      Vue.set(reward, 'isSelected', true);
      this.selectedReward = reward;

      if (reward.offerType && reward.offerType === "ContextualBag"){
        this.rewardButtonText = `REDEEM FOR ${reward.points} POINTS`
      } else {
        this.rewardButtonText = 'USE REWARD'
      }

      this.onRewardCtaVisibleInModal();
    }

    if (position >= 0) {
      this.position = position;
    }
  }

  @Watch('visible')
  async onVisibleUpdated(visible: boolean) {
    if (!visible) {
      this.hasTrackedModalRewardCtaPresentation = false;
      return;
    }

    this.hasTrackedModalRewardCtaPresentation = false;

    if (visible && !this.bagContent) {
      this.bagContent = this.$services.config.getBagContent()!;
      this.CombinedWalletAndPointsRewards();
    }

    this.onRewardCtaVisibleInModal();
  }

  isSupportedRewardCtaText(text: string): boolean {
    const normalized = (text || '').replace(/\s+/g, ' ').trim().toUpperCase();
    const pointsCtaPattern = /^REDEEM(?: NOW)? FOR [\d,]+(?:\.\d+)? (?:POINTS|PTS)$/;

    return normalized === 'REDEEM YOUR POINTS' || pointsCtaPattern.test(normalized);
  }

  private getModalRewardCtaCandidates(): string[] {
    // Track presentation based on the actual primary CTA button that is shown.
    // While the button still reads "USE REWARD" (no contextual points reward selected),
    // the rewardsViewed (bag-rewards-viewed) event must not fire.
    return [this.rewardButtonText];
  }

  onRewardCtaVisibleInModal() {
    if (this.hasTrackedModalRewardCtaPresentation) {
      return;
    }

    const hasSupportedText = this.getModalRewardCtaCandidates().some(text => this.isSupportedRewardCtaText(text));
    if (!hasSupportedText) {
      return;
    }

    this.hasTrackedModalRewardCtaPresentation = true;
    this.onRewardPresented();
  }

  onRewardPresented() {
    const reward = this.rewards[0];
    const showingRewardsCarousel = this.rewards.length + this.bagRewards.length > 1;

    this.$services.analytics.send(new AccountManagementEvent(
      InteractionTrackingType.BagRewardsViewed,
      undefined,
      showingRewardsCarousel ? undefined : reward?.promotionName,
      'Bag',
      showingRewardsCarousel ? undefined : reward?.promotionCode,
      'rewards-bag',
      showingRewardsCarousel ? undefined : reward?.campaign,
    ));
  }

  displayRewardToast(visible: boolean) {
    this.$services.ui.setBagRewardsToastVisible(visible);
  }
  
  private handleAddToBagException(e: any) {
    if (!(e instanceof OrderApiException)) {
      throw e;
    }

    const error = e.errors[0];
    if (!error) {
      throw e;
    }

    switch (error.errorCode) {
      case OrderErrorCode.RewardExceedsLimit:
        this.$services.ui.setModalVisibility('toast', true, {
          title: '',
          body: 'Only one reward per order.',
          bodyOnly: true,
        });
        Vue.nextTick(() => {
          setTimeout(() => {
            this.$services.ui.nextFocus('reward-heading');
          }, 6000);
        });
        break;
    }
  }

  onClickTermsAndConditions(reward: any) {
    const onBack = () => {
      this.$services.ui.setModalVisibility('rewardsListModal', true, {
        rewards: this.combinedRewards
      });
      this.$services.ui.setModalVisibility('rewardTermsAndConditions', false);
    };
    const onClose = () => {
      this.$services.ui.setModalVisibility('rewardsListModal', false);
    };
    this.$services.ui.setModalVisibility('rewardTermsAndConditions', true, { termsAndConditions: reward.terms ?? reward.termsAndConditions, onBack, onClose });
  }

  onClose() {
    this.$services.ui.setAppPepperVisibility(false);
    this.$services.ui.setModalVisibility('rewardsListModal', false);
    this.clearSelectedRewards();
  }

  getFormattedExpirationDate(date: string) {
    const rewardExpirationDate = new Date(date);
    const today = new Date();
    let tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    if(rewardExpirationDate.getDate() === today.getDate()){
      return 'Today'
    }
    if(rewardExpirationDate.getDate() === tomorrow.getDate()){
      return 'Tomorrow'
    }
    return moment(rewardExpirationDate, 'D/MM/YYYY').format('dddd, MMM. DD');
  }
}
